import { Directive, ElementRef, Renderer2, Inject, Input, HostBinding } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { Logger } from './logger';
import { ViewportSize } from './viewport-size';

let nextImageId = 1000;

/**
 * Implements responsive image loading for background-image
 */
@Directive({
    selector: '[responsiveBackgroundImage]',
    inputs: ['responsiveBackgroundImage']
})
export class ResponsiveBackgroundImageDirective {
    constructor(
        @Inject(DOCUMENT)
        private _doc : any,
        private elementRef : ElementRef,
        private renderer : Renderer2,
        private viewportSize : ViewportSize,
        private domSanitizer : DomSanitizer
    ) {
    }

    private _url : string;

    @Input()
    get responsiveBackgroundImage(): any {
        return this._url;
    }

    set responsiveBackgroundImage(url : any) {
        if (this._url == url)
            return;
        
        let alreadyStarted = this._url != null;

        this._url = url;

        if (this._initialized && this._visible) {
            this.fadeSwitch();
        }
    }

    @HostBinding(`class.responsive-background`)
    primaryClass : boolean = true;

    @HostBinding(`class.switching-background`)
    switching : boolean = false;
    
    async fadeSwitch() {
        this.switching = true;
        // await sleep(500);
        this.setupImage();
        this.switching = false;
    }

    /**
     * Whether to use the Intersection Observer API to detect when the image is nearly visible before 
     * starting the load process. On server side builds, this does nothing. Instead the developer should 
     * take care not to send any elements that are invisible at all.
     */
    @Input()
    whenVisible : boolean = true;

    @Input()
    widthFactor : number = 1.0;

    @HostBinding(`attr.data-image-id`) 
    imageId : any;

    @HostBinding(`style.background-image`) 
    backgroundImageStyle : any;

    _initialized : boolean = false;

    ngOnInit() {
        this._initialized = true;
        this.imageId = nextImageId++;
        this.run();
    }

    run() {
        if (typeof window === 'undefined')
            this.runForSSR();
        else 
            this.runForBrowser();
    }

    private _visible : boolean = false;

    /**
     * Implementation for the browser. Uses the client's full screen size and does not do 
     * media query based resizing.
     */
    private runForBrowser() {
        if (this.whenVisible && typeof IntersectionObserver !== 'undefined') {
            let observer : IntersectionObserver;
            
            observer = new IntersectionObserver((entries) => {
                if (!entries[0] || (entries[0].intersectionRatio == 0 && !entries[0]['isIntersecting']))
                    return;

                this.setupImage();
                observer.unobserve(this.elementRef.nativeElement);
            }, {
                rootMargin: '1000px',
                threshold: 0
            });

            observer.observe(this.elementRef.nativeElement);
        } else {
            this.setupImage();
        }
    }

    /**
     * Setup the image to be shown on the background-image property.
     * If `hurry` is set, will use the XS sized image along with the 
     * correctly sized image to ensure that something renders right away,
     * and then is replaced by the right one
     * @param hurry 
     */
    private setupImage() { 
        this._visible = true;
        let value = this.responsiveBackgroundImage;
        let widthFactor = this.widthFactor;

        // If its invalid, return a blank gif
        if (!value || (typeof value !== 'string' && !value.urls))
            value = 'url(data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==)';

        if (value.urls) {
            let assetSize = this.viewportSize.selectAssetSize(widthFactor);

            // Return the appropriate size URL, or if the asset does not 
            // have the needed size, return the absolute smallest (because this 
            // needs to be handled by a developer)

            let neededImage = `url(${value.urls[assetSize] || value.urls['xs']})`;

            this.backgroundImageStyle = this.domSanitizer.bypassSecurityTrustStyle(
                neededImage
            );
        } else if (/\/\/images\.contentful/.test(value)) {
            // Contentful URL. These have some special properties!

            let url = value;

            if (typeof window !== 'undefined') {
                let idealWidth = window.innerWidth * widthFactor + 300;

                url += `?w=${idealWidth}`;
            }

            this.backgroundImageStyle = this.domSanitizer.bypassSecurityTrustStyle(`url(${url})`);

        } else {
            this.backgroundImageStyle = this.domSanitizer.bypassSecurityTrustStyle(`url(${value})`);
        }
    }

    /**
     * Implementation for the server. Uses media queries to ensure the user agent will download the appropriate image 
     * based on the viewport size. 
     */
    private runForSSR() {

        let rbi = this.responsiveBackgroundImage;
        if (!rbi)
            return;

        if (typeof rbi != 'string' && !rbi.urls)
            return;

        let style = this.renderer.createElement('style');
        let rules = [];

        let sizes = {
            xs: [0, 250],
            sm: [251, 551],
            sd: [552, 1137],
            hd720: [1138, 1280],
            hd: [1281, 1920],
            xhd: [1921, /*2560*/8000],
            original: [2560, 8000],     // TODO: should be 'xxhd' but we need to populate this class of thumbnails
            xxhd: [0, 0],
        };

        let inverseWidthFactor = 1 / this.widthFactor;

        if (rbi.urls) {
            for (let size in rbi.urls) {
                let url = rbi.urls[size]
                let sizeData = sizes[size];
                
                rules.push(
                    `@media (min-width: ${inverseWidthFactor * sizeData[0]}px) ` 
                    + `and (max-width: ${inverseWidthFactor * sizeData[1]}px) {` 
                        + `[data-image-id="${this.imageId}"] {` 
                            + `background-image: url(${url});` 
                        + `}` 
                    + `}`
                );
            }
        } else {
            rules.push(
                `[data-image-id="${this.imageId}"] {` 
                    + `background-image: url(${rbi});` 
                + `}`
            );
        }

        this.renderer.setAttribute(style, "data-image-id", `${this.imageId}`);
        this.renderer.appendChild(style, 
            this.renderer.createText(
                rules.join("\n")
            )
        );

        this.renderer.appendChild(this._doc.head, style);

    }
}